import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Mail, 
  Sparkles, 
  CheckCircle, 
  AlertCircle, 
  Code, 
  Eye, 
  Lock, 
  Copy,
  Edit3,
  RotateCcw,
  Settings,
  ToggleLeft
} from 'lucide-react';

const EmailTemplatesGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Email Templates Guide</h1>
          <p className="text-lg text-gray-600">
            Customize automated emails sent to your customers
          </p>
        </div>

        {/* What Are Email Templates */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Mail className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">What Are Email Templates?</h2>
              <p className="text-gray-700">
                Email templates are pre-written emails that SuddyFlow automatically sends to customers when certain events happen - 
                like creating an invoice, receiving payment, or scheduling a job. Pro and Enterprise users can customize these emails 
                to match their business's voice and style.
              </p>
            </div>
          </div>
        </div>

        {/* Two Views Explanation */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Understanding the Two Views</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-700 mb-4">
              The Email Templates page has two tabs to keep things organized:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* System Templates */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="text-gray-600" size={20} />
                  <h3 className="font-semibold text-gray-900">System Templates</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Professional templates we've created for you. These are ready to use out of the box.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• View all available template types</li>
                  <li>• Preview how emails will look</li>
                  <li>• Click <Copy size={14} className="inline" /> to customize</li>
                  <li>• Toggle active/disabled status</li>
                </ul>
              </div>
              
              {/* My Custom */}
              <div className="border border-primary-200 bg-primary-50/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="text-primary-600" size={20} />
                  <h3 className="font-semibold text-gray-900">My Custom</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Templates you've personalized for your business. Your custom version is always used instead of the system default.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• View only your customized templates</li>
                  <li>• Click <Edit3 size={14} className="inline" /> to edit</li>
                  <li>• Click <RotateCcw size={14} className="inline" /> to reset to default</li>
                  <li>• Toggle active/disabled status</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-800">
                <strong>How it works:</strong> When you customize a template, it moves from "System Templates" to "My Custom". 
                If you reset it, it moves back. You'll never see duplicates - each template type appears in only one place.
              </p>
            </div>
          </div>
        </div>

        {/* Available Templates */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Available Email Templates</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {/* Job Templates */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">📅 Job Emails</h3>
                <div className="space-y-2 ml-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Job Confirmation</p>
                      <p className="text-sm text-gray-600">Sent when you schedule a job (if enabled in Settings)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Service Prep</p>
                      <p className="text-sm text-gray-600">Sent 48 hours before service with preparation instructions</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Appointment Reminder <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded ml-1">Pro</span></p>
                      <p className="text-sm text-gray-600">Sent 24 hours before scheduled jobs</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Job Completed</p>
                      <p className="text-sm text-gray-600">Sent when you mark a job as complete</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Templates */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">📄 Invoice Emails</h3>
                <div className="space-y-2 ml-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Invoice Sent</p>
                      <p className="text-sm text-gray-600">Sent when you create an invoice with email enabled</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Templates */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">💰 Payment Emails</h3>
                <div className="space-y-2 ml-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Payment Received</p>
                      <p className="text-sm text-gray-600">Sent when you record a payment</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Payment Reminders <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded ml-1">Pro</span></p>
                      <p className="text-sm text-gray-600">Sent at 3, 7, and 14 days overdue</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Review & Marketing */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">⭐ Reviews & Marketing</h3>
                <div className="space-y-2 ml-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Review Request <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded ml-1">Pro</span></p>
                      <p className="text-sm text-gray-600">Sent after job completion asking for a review</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Re-engagement Campaigns <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded ml-1">Enterprise</span></p>
                      <p className="text-sm text-gray-600">Win-back emails for inactive customers (90, 180, 365 days)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Seasonal Reminders <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded ml-1">Enterprise</span></p>
                      <p className="text-sm text-gray-600">Promote seasonal services to past customers</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How to Customize */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-primary-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="text-primary-600" size={24} />
              How to Customize a Template
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-900">
                <strong>Requires:</strong> Pro or Enterprise subscription (or during free trial)
              </p>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                1
              </div>
              <div className="flex-1">
                <p className="text-gray-700">
                  Go to <strong>Settings → Email Templates</strong> and make sure you're on the <strong>"System Templates"</strong> tab
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                2
              </div>
              <div className="flex-1">
                <p className="text-gray-700">
                  Find the template you want to customize and click the <Copy size={16} className="inline text-gray-500" /> <strong>copy icon</strong>
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                3
              </div>
              <div className="flex-1">
                <p className="text-gray-700">
                  Edit the <strong>subject line</strong> and <strong>email body</strong> (HTML supported)
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                4
              </div>
              <div className="flex-1">
                <p className="text-gray-700">
                  Use <strong>merge fields</strong> like <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">{'{{CustomerFirstName}}'}</code> to personalize emails
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                5
              </div>
              <div className="flex-1">
                <p className="text-gray-700">
                  Click <strong>"Create Custom Template"</strong> to save. Your template moves to "My Custom" and will be used for all future emails of that type.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Icons Reference */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Button Reference</h2>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="p-2 bg-white rounded-lg border border-gray-200">
                  <Eye size={18} className="text-gray-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Preview</p>
                  <p className="text-sm text-gray-600">See how the email looks with sample data</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="p-2 bg-white rounded-lg border border-gray-200">
                  <Copy size={18} className="text-gray-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Customize</p>
                  <p className="text-sm text-gray-600">Create your own version of a system template</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-primary-50 rounded-lg">
                <div className="p-2 bg-white rounded-lg border border-primary-200">
                  <Edit3 size={18} className="text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Edit</p>
                  <p className="text-sm text-gray-600">Modify your custom template</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="p-2 bg-white rounded-lg border border-gray-200">
                  <RotateCcw size={18} className="text-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Reset</p>
                  <p className="text-sm text-gray-600">Delete your custom version and use the system default</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enabling/Disabling Templates */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <ToggleLeft className="text-gray-600" size={24} />
              Turning Templates On/Off
            </h2>
          </div>
          <div className="p-6">
            <p className="text-gray-700 mb-4">
              Click the <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium"><CheckCircle size={12} /> Active</span> or 
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs font-medium ml-1">Disabled</span> badge 
              to toggle a template's status.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                <p className="font-medium text-green-900 mb-1">Active</p>
                <p className="text-sm text-green-800">
                  Email will be sent when triggered (if also enabled in Settings → Email Settings)
                </p>
              </div>
              <div className="border border-gray-200 bg-gray-50 rounded-lg p-4">
                <p className="font-medium text-gray-700 mb-1">Disabled</p>
                <p className="text-sm text-gray-600">
                  Email will never be sent, even if the trigger event happens
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-amber-900 font-medium mb-1">Two Places to Control Emails</p>
                  <p className="text-sm text-amber-800">
                    <strong>Settings → Email Settings:</strong> Master toggles for email types (e.g., "Send Job Confirmations")
                    <br />
                    <strong>Email Templates page:</strong> Enable/disable specific templates
                    <br /><br />
                    Both must be enabled for an email to send automatically.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Merge Fields */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Code className="text-blue-600" size={24} />
              Merge Fields Reference
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <p className="text-gray-700">
              Merge fields are placeholders that get replaced with real data when emails are sent. 
              Use the format: <code className="bg-gray-100 px-2 py-0.5 rounded">{'{{FieldName}}'}</code>
            </p>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Customer & Business</h3>
              <div className="grid md:grid-cols-2 gap-2">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{CustomerFirstName}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Customer's first name</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{CustomerName}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Customer's full name</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{BusinessName}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Your business name</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{BusinessPhone}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Your business phone</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Job Information</h3>
              <div className="grid md:grid-cols-2 gap-2">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{JobDate}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Scheduled date</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{JobTime}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Scheduled time</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{JobAddress}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Service address</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{JobServices}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Services being performed</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Invoice & Payment</h3>
              <div className="grid md:grid-cols-2 gap-2">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{InvoiceNumber}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Invoice number</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{InvoiceAmount}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Total amount due</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{InvoiceDueDate}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Payment due date</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{PaymentAmount}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Amount received</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Links</h3>
              <div className="grid md:grid-cols-2 gap-2">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{GoogleReviewUrl}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Your Google review link</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="text-blue-600 font-mono text-sm">{'{{UnsubscribeLink}}'}</code>
                  <p className="text-xs text-gray-600 mt-1">Email opt-out link (required)</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Example:</h4>
              <div className="bg-white rounded p-3 font-mono text-sm text-gray-700">
                <p>Hi {'{{CustomerFirstName}}'},</p>
                <p className="mt-2">Your service is scheduled for {'{{JobDate}}'} at {'{{JobTime}}'}.</p>
                <p className="mt-2">See you soon!</p>
              </div>
              <p className="text-sm text-blue-700 mt-3">
                <strong>Becomes:</strong> "Hi John, Your service is scheduled for Monday, December 16, 2025 at 9:00 AM. See you soon!"
              </p>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📧 Email Best Practices</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-green-900 mb-1">Keep Subject Lines Short</h3>
              <p className="text-sm text-green-800">
                Aim for under 50 characters. Mobile devices cut off long subjects.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-green-900 mb-1">Use a Friendly Tone</h3>
              <p className="text-sm text-green-800">
                Write like you're talking to a neighbor. "Thanks for your payment!" beats "We acknowledge receipt of funds."
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-green-900 mb-1">Preview Before Saving</h3>
              <p className="text-sm text-green-800">
                Always click the <Eye size={14} className="inline" /> preview button to see how your email looks with real data.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-green-900 mb-1">Keep the Unsubscribe Link</h3>
              <p className="text-sm text-green-800">
                Always include <code className="bg-green-100 px-1 rounded text-xs">{'{{UnsubscribeLink}}'}</code> in marketing emails. It's required by law and builds trust.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-green-900 mb-1">Mobile-Friendly Design</h3>
              <p className="text-sm text-green-800">
                70% of emails are read on phones. Keep paragraphs short and use large, tappable buttons for links.
              </p>
            </div>
          </div>
        </div>

        {/* Common Questions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Common Questions</h2>
          
          <div className="space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What if I mess up a template?
              </h3>
              <p className="text-sm text-gray-700">
                Click the <RotateCcw size={14} className="inline text-gray-400" /> reset button to delete your custom version and go back to the professional system default.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Why isn't my customized email being sent?
              </h3>
              <p className="text-sm text-gray-700">
                Check two things: (1) The template must be "Active" on the Email Templates page, and (2) The email type must be enabled in Settings → Email Settings.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I add my logo to emails?
              </h3>
              <p className="text-sm text-gray-700">
                Yes! Use an HTML image tag: <code className="bg-gray-100 px-1 rounded text-xs">{'<img src="https://yoursite.com/logo.png" />'}</code>. 
                The image must be hosted online (like on your website).
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Do I need to know HTML?
              </h3>
              <p className="text-sm text-gray-700">
                Not required! System templates work great as-is. For advanced formatting, basic HTML helps. 
                Tags like <code className="bg-gray-100 px-1 rounded text-xs">{'<strong>'}</code> (bold) and 
                <code className="bg-gray-100 px-1 rounded text-xs ml-1">{'<p>'}</code> (paragraph) go a long way.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What happens if a customer doesn't have an email?
              </h3>
              <p className="text-sm text-gray-700">
                The email is simply skipped - no errors. Everything else (invoices, payments, jobs) works normally.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Why do merge fields show as {'{{CustomerName}}'} in the preview?
              </h3>
              <p className="text-sm text-gray-700">
                Make sure you're using the exact field name with double curly braces. Capitalization matters! 
                <code className="bg-gray-100 px-1 rounded text-xs ml-1">{'{{CustomerName}}'}</code> works, but 
                <code className="bg-gray-100 px-1 rounded text-xs ml-1">{'{{customername}}'}</code> won't.
              </p>
            </div>
          </div>
        </div>

        {/* Need Help */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6 text-center mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Need Help with Email Templates?</h3>
          <p className="text-gray-700 mb-4">
            We can help you craft the perfect emails for your business!
          </p>
          <a
            href="mailto:support@suddyflow.com"
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Mail size={18} />
            Email Support
          </a>
        </div>

        {/* Back to Help Center */}
        <div className="text-center">
          <button
            onClick={() => navigate('/help')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatesGuide;